import { useGetRecordBySlugQuery, useGetRecordQuery } from "@/api/api";
import { useParams } from "react-router";
import PageContainer from "./PageContainer";
import RecordView from "./RecordView";
import { deserializeRecord } from "@/common/record";
import RecordSheet from "./RecordSheet";
import { Button } from "../button";
import { Edit } from "lucide-react";
import { useCanEditRecord, useCanonicalPath } from "@/api/hooks";

/**
 * Serves both record URLs:
 *   /{user}/-/{slug}   the readable one
 *   /record/{id}       the legacy permalink, and the only form a record
 *                      without a slug has
 */
export default function RecordPage() {
  const { recordID, userID, recordSlug } = useParams<{
    recordID: string;
    userID: string;
    recordSlug: string;
  }>();

  const bySlug = useGetRecordBySlugQuery(
    { userRef: userID || "", recordRef: recordSlug || "" },
    { skip: !userID || !recordSlug },
  );
  const byID = useGetRecordQuery(recordID || "", { skip: !recordID });

  const recordRequest = recordID ? byID : bySlug;

  // Set when the URL used a stale slug, or an id for a record that has a
  // slug. Hooks must run before the early returns below.
  useCanonicalPath(recordRequest.data?.canonical_path);
  // Before the early returns below -- hooks cannot run conditionally.
  const canEdit = useCanEditRecord(recordRequest.data);

  if (!recordID && !recordSlug) {
    return null;
  }
  if (recordRequest.isLoading) {
    return <div>Loading...</div>;
  }

  if (recordRequest.isError) {
    return <div>Error loading record</div>;
  }

  const recordAPI = recordRequest.data;
  if (!recordAPI) {
    return null;
  }

  const record = deserializeRecord(recordAPI);
  if (!record) return null;

  return (
    <PageContainer>
      <RecordView record={record} showTitle />
      {canEdit && (
        <div className="flex justify-end gap-2">
          <RecordSheet record={record} title="Edit record">
            <Button variant="ghost">
              <Edit />
            </Button>
          </RecordSheet>
        </div>
      )}
    </PageContainer>
  );
}
